import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CAROUSEL_STATEMENT, CAROUSEL_SUB, CAROUSEL_TITLE, ebookPages } from '../data/poolContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const SWIPE_THRESHOLD = 50;
const NAV_BTN = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-aqua/40 text-aqua transition-colors duration-150 ease-out hover:bg-aqua hover:text-abyss-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-aqua';
export function EbookCarouselSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = ebookPages.length;
  const go = (next: number, dir?: number) => {
    const target = (next + total) % total;
    setDirection(dir ?? (target > index ? 1 : -1));
    setIndex(target);
  };
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -SWIPE_THRESHOLD || info.velocity.x < -400) go(index + 1, 1);else if (info.offset.x > SWIPE_THRESHOLD || info.velocity.x > 400) go(index - 1, -1);
  };
  const current = ebookPages[index];
  return <section className="w-full overflow-hidden border-y border-pool/20 bg-black py-14 sm:py-20" aria-labelledby="carousel-title" aria-roledescription="carousel" data-id="element-76" data-mp="37j9unlsl9srr">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-5" data-id="element-77" data-mp="3hdi955ksbl6x">
        <h2 id="carousel-title" className="text-center font-display text-[2.3rem] font-bold uppercase leading-none text-frost-50 sm:text-[3.2rem]" data-id="element-78" data-mp="3ch1jg6xxsyrq">
          {CAROUSEL_TITLE}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-[14px] leading-snug text-body sm:text-[15px]" data-id="element-79" data-mp="3mfr2i31kkp9k">
          {CAROUSEL_SUB}
        </p>

        <div className="mt-8 flex w-full items-center justify-center gap-6" data-id="element-80" data-mp="1s0erov2f7o7a">
          <button type="button" onClick={() => go(index - 1, -1)} className={`hidden sm:flex ${NAV_BTN}`} data-id="element-81" data-mp="1s2hrg6tdkvp7">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-82" data-mp="3r7j7g682cjr5" />
            <span className="sr-only" data-id="element-83" data-mp="3w4hzmj862gmp">Previous page</span>
          </button>

          <div className="relative mx-auto aspect-[9/16] w-full max-w-[min(100%,24rem)] overflow-hidden rounded-xl bg-abyss-900 sm:max-w-[26rem]" data-id="element-84" data-mp="4x5ig47aa36s">
            <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-85" data-mp="dymsp5y0fank">
              <motion.img key={current.image} src={current.image} alt={current.alt} initial={{
              opacity: 0,
              x: direction * 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -40
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.25} onDragEnd={onDragEnd} draggable={false} className="absolute inset-0 h-full w-full cursor-grab touch-pan-y select-none object-contain active:cursor-grabbing" data-id="element-86" data-mp="dwk3go4olljw" />
            </AnimatePresence>
          </div>

          <button type="button" onClick={() => go(index + 1, 1)} className={`hidden sm:flex ${NAV_BTN}`} data-id="element-87" data-mp="2d520sk95i8gh">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-88" data-mp="287a99rcf4oct" />
            <span className="sr-only" data-id="element-89" data-mp="35uqyooqkdsdv">Next page</span>
          </button>
        </div>

        {/* Mobile controls: arrows + dots */}
        <div className="mt-5 flex items-center justify-center gap-4 sm:hidden" data-id="element-90" data-mp="30wh7ep7ynbxj">
          <button type="button" onClick={() => go(index - 1, -1)} className={NAV_BTN} data-id="element-91" data-mp="2w3e800gi589f">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-92" data-mp="2r5us6w45onfd" />
            <span className="sr-only" data-id="element-93" data-mp="3okgvtihsuq8y">Previous page</span>
          </button>
          <div className="flex items-center gap-1.5" data-id="element-94" data-mp="3omjuss0xjtpt">
            {ebookPages.map((page, i) => <button key={page.image} type="button" onClick={() => go(i)} aria-current={i === index} className="flex h-6 w-4 items-center justify-center" data-id="element-95" data-mp="3js76a3kljs77" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-4 bg-aqua' : 'w-2 bg-abyss-500'}`} data-id="element-96" data-mp="2ueurgl4nw7ht" />
                <span className="sr-only" data-id="element-97" data-mp="3n4unrvl3kc92">Show page {i + 1}</span>
              </button>)}
          </div>
          <button type="button" onClick={() => go(index + 1, 1)} className={NAV_BTN} data-id="element-98" data-mp="3dcyo4g3d4e2q">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-99" data-mp="3daujfvxn3b1e" />
            <span className="sr-only" data-id="element-100" data-mp="3i9p28xvlwzli">Next page</span>
          </button>
        </div>

        <p className="mt-3 text-center text-[11px] font-medium tracking-[0.14em] text-muted" aria-live="polite" data-id="element-101" data-mp="9unku3u303zh">
          {index + 1} / {total}
        </p>

        {/* Desktop thumbnails */}
        <ul className="mt-6 hidden flex-wrap justify-center gap-3 sm:flex" data-id="element-102" data-mp="1zbizxjnsprjt">
          {ebookPages.map((page, i) => <li key={page.image} data-id="element-103" data-mp="1ubwn3bbvqdbj" data-mpi={i}>
              <button type="button" onClick={() => go(i)} aria-current={i === index} className={`block w-[4.5rem] overflow-hidden rounded-md border bg-abyss-900 transition-[border-color,opacity] duration-150 ease-out ${i === index ? 'border-aqua opacity-100' : 'border-abyss-600 opacity-50 hover:opacity-100'}`} data-id="element-104" data-mp="285xe1iwlaw1c">
                <img src={page.image} alt="" className="aspect-[9/16] w-full object-contain" loading="lazy" data-id="element-105" data-mp="1pfpgdgbnsxsj" />
                <span className="sr-only" data-id="element-106" data-mp="1ke9y1w0v95du">Show page {i + 1}</span>
              </button>
            </li>)}
        </ul>

        <p className="mt-10 text-center font-display text-[1.5rem] font-bold uppercase text-aqua sm:text-[1.8rem]" data-id="element-107" data-mp="30vwaw0h0zxgk">
          {CAROUSEL_STATEMENT}
        </p>
      </div>
    </section>;
}